import React, { useState, useEffect, memo, useCallback, useRef } from 'react';
import axios from 'axios';
import ReactCountryFlag from 'react-country-flag';
import AdminSidebarRaw from '../Components/AdminSidebar';
import { API_BASE_URL } from '../config';
import '../Styles/SocialMedia.css';

const AdminSidebar = memo(AdminSidebarRaw);

const countryOptions = [
    { value: '+961', label: 'Lebanon', iso: 'LB' },
    { value: '+971', label: 'UAE', iso: 'AE' },
    { value: '+966', label: 'Saudi Arabia', iso: 'SA' },
    { value: '+20', label: 'Egypt', iso: 'EG' },
    { value: '+962', label: 'Jordan', iso: 'JO' },
    { value: '+965', label: 'Kuwait', iso: 'KW' },
    { value: '+974', label: 'Qatar', iso: 'QA' },
    { value: '+973', label: 'Bahrain', iso: 'BH' },
    { value: '+968', label: 'Oman', iso: 'OM' },
    { value: '+1', label: 'USA', iso: 'US' },
    { value: '+44', label: 'UK', iso: 'GB' },
    { value: '+33', label: 'France', iso: 'FR' },
    { value: '+49', label: 'Germany', iso: 'DE' },
    { value: '+90', label: 'Turkey', iso: 'TR' },
    { value: '+963', label: 'Syria', iso: 'SY' },
    { value: '+964', label: 'Iraq', iso: 'IQ' },
    { value: '+967', label: 'Yemen', iso: 'YE' }
];

const SocialMedia = () => {
    const [items, setItems] = useState([]);
    const [formData, setFormData] = useState({
        whatsapp: '',
        facebook: '',
        instagram: '',
        countryCode: '+961',
        phoneNumber: ''
    });
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);




    const singleItem = items[0] || null;

    const fetchSocialMedia = useCallback(async () => {
        const adminToken = localStorage.getItem('adminToken');
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/admin/social-media`, {
                headers: { Authorization: `Bearer ${adminToken}` }
            });
            const data = response.data || [];
            setItems(data);
            if (data.length > 0) {
                const item = data[0];
                setEditingId(item.id);

                // Try to parse WhatsApp number and country code from URL
                let countryCode = '+961';
                let phoneNumber = '';
                if (item.whatsapp && item.whatsapp.includes('wa.me/')) {
                    const fullNumber = item.whatsapp.split('wa.me/')[1];
                    const matchedCountry = countryOptions.find(c => fullNumber.startsWith(c.value.replace('+', '')));
                    if (matchedCountry) {
                        countryCode = matchedCountry.value;
                        phoneNumber = fullNumber.replace(matchedCountry.value.replace('+', ''), '');
                    } else {
                        phoneNumber = fullNumber;
                    }
                }

                setFormData({
                    whatsapp: item.whatsapp || '',
                    facebook: item.facebook || '',
                    instagram: item.instagram || '',
                    countryCode,
                    phoneNumber
                });
            } else {
                setFormData({ whatsapp: '', facebook: '', instagram: '', countryCode: '+961', phoneNumber: '' });
                setEditingId(null);
            }
            setError(null);
        } catch (err) {
            console.error('Error fetching social media:', err);
            setError('Failed to load social media settings.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSocialMedia();
    }, [fetchSocialMedia]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowCountryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const resetForm = () => {
        setFormData({
            whatsapp: '',
            facebook: '',
            instagram: '',
            countryCode: '+961',
            phoneNumber: ''
        });
        setEditingId(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.whatsapp && !formData.facebook && !formData.instagram) {
            return setError('At least one link is required.');
        }

        try {
            setSaving(true);
            setError(null);

            // Construct WhatsApp URL from phone components
            let sanitizedPhone = formData.phoneNumber.replace(/\D/g, '');
            // Remove leading zero if present
            if (sanitizedPhone.startsWith('0')) {
                sanitizedPhone = sanitizedPhone.substring(1);
            }

            const finalWhatsApp = sanitizedPhone
                ? `https://wa.me/${formData.countryCode.replace('+', '')}${sanitizedPhone}`
                : '';

            const submitData = {
                whatsapp: finalWhatsApp,
                facebook: formData.facebook,
                instagram: formData.instagram
            };

            const targetId = editingId || singleItem?.id;
            const adminToken = localStorage.getItem('adminToken');
            const headers = { Authorization: `Bearer ${adminToken}` };
            if (targetId) {
                await axios.put(`${API_BASE_URL}/admin/social-media/${targetId}`, submitData, { headers });
            } else {
                await axios.post(`${API_BASE_URL}/admin/social-media`, submitData, { headers });
            }

            await fetchSocialMedia();
        } catch (err) {
            console.error('Error saving social media:', err);
            setError(err.response?.data?.message || 'Unable to save social media settings.');
        } finally {
            setSaving(false);
        }
    };


    return (
        <div className="socialmedia-container">
            <AdminSidebar />
            <main className="socialmedia-main">
                <div className="socialmedia-content-grid">
                    <section className="socialmedia-form-card">
                        <h2>
                            {editingId || singleItem ? 'Edit Social Media' : 'Add Social Media'}
                        </h2>
                        <form onSubmit={handleSubmit}>
                            <label>
                                WhatsApp Number
                                <div className="socialmedia-phone-input-wrapper">
                                    <div className="country-selector" ref={dropdownRef}>
                                        <button
                                            type="button"
                                            className="country-dropdown-button"
                                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                        >
                                            <ReactCountryFlag
                                                countryCode={countryOptions.find(c => c.value === formData.countryCode)?.iso}
                                                svg
                                                style={{ width: '20px', height: '15px' }}
                                            />
                                            <span>{formData.countryCode}</span>
                                        </button>
                                        {showCountryDropdown && (
                                            <div className="country-dropdown-list">
                                                {countryOptions.map((country) => (
                                                    <button
                                                        key={country.iso}
                                                        type="button"
                                                        className="country-dropdown-item"
                                                        onClick={() => {
                                                            setFormData({ ...formData, countryCode: country.value });
                                                            setShowCountryDropdown(false);
                                                        }}
                                                    >
                                                        <ReactCountryFlag countryCode={country.iso} svg />
                                                        <span>{country.label} ({country.value})</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <input
                                        type="tel"
                                        className="socialmedia-phone-input"
                                        placeholder="Phone number"
                                        value={formData.phoneNumber}
                                        onChange={(e) => {
                                            let val = e.target.value.replace(/\D/g, '');
                                            if (val.startsWith('0')) val = val.substring(1);
                                            setFormData({ ...formData, phoneNumber: val });
                                        }}
                                    />
                                </div>
                                <div className="socialmedia-input-hint">
                                    Automatically creates link: https://wa.me/{formData.countryCode.replace('+', '')}{formData.phoneNumber.replace(/\D/g, '').startsWith('0') ? formData.phoneNumber.replace(/\D/g, '').substring(1) : formData.phoneNumber.replace(/\D/g, '') || 'number'}
                                </div>
                            </label>
                            <label>
                                Facebook URL
                                <input
                                    type="text"
                                    placeholder="https://facebook.com/yourpage"
                                    value={formData.facebook}
                                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                                />
                            </label>
                            <label>
                                Instagram URL
                                <input
                                    type="text"
                                    placeholder="https://instagram.com/yourprofile"
                                    value={formData.instagram}
                                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                                />
                            </label>
                            {error && <div className="socialmedia-error">{error}</div>}
                            <div className="socialmedia-form-actions">
                                <button type="submit" className="socialmedia-button primary" disabled={saving}>
                                    {saving ? 'Saving...' : (editingId || singleItem) ? 'Update Links' : 'Create Links'}
                                </button>
                                <button type="button" className="socialmedia-button secondary" onClick={resetForm}>
                                    Clear
                                </button>
                            </div>
                        </form>
                    </section>

                    <section className="socialmedia-list-card">
                        <h2>Current Configuration</h2>
                        {loading ? (
                            <div className="socialmedia-loading">Loading configuration...</div>
                        ) : items.length === 0 ? (
                            <div className="socialmedia-empty">
                                <p>No social media links configured.</p>
                                <p>Fill the form to set up your links.</p>
                            </div>
                        ) : (
                            <div className="socialmedia-table-wrapper">
                                <table className="socialmedia-table">
                                    <thead>
                                        <tr>
                                            <th>Platform</th>
                                            <th>Value / Link</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td><strong>WhatsApp</strong></td>
                                            <td>
                                                <div className="social-link-cell">
                                                    {singleItem?.whatsapp?.includes('wa.me/') && (
                                                        <ReactCountryFlag
                                                            countryCode={
                                                                countryOptions.find(c =>
                                                                    singleItem.whatsapp.split('wa.me/')[1]?.startsWith(c.value.replace('+', ''))
                                                                )?.iso || 'UN'
                                                            }
                                                            svg
                                                        />
                                                    )}
                                                    {singleItem?.whatsapp?.includes('wa.me/')
                                                        ? '+' + singleItem.whatsapp.split('wa.me/')[1]
                                                        : (singleItem?.whatsapp || '-')}
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td><strong>Facebook</strong></td>
                                            <td>{singleItem?.facebook || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td><strong>Instagram</strong></td>
                                            <td>{singleItem?.instagram || '-'}</td>
                                        </tr>
                                    </tbody>
                                </table>

                            </div>
                        )}
                    </section>
                </div>
            </main>
        </div>
    );
};

export default SocialMedia;
